export const CAMPAIGN_REPORTING_CONTRACT_VERSION =
  "campaign_reporting_lifecycle_v1";

const isoDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

export const campaignReportingContract = (campaign) => {
  const contract = campaign?.reportingContract || campaign?.reporting_contract;
  return contract && typeof contract === "object" ? contract : null;
};

export const campaignSelectorLabel = (campaign) => {
  const name = String(campaign?.name || campaign?.campaignName || "Campaign").trim();
  const status = String(
    campaignReportingContract(campaign)?.selectorStatusLabel || ""
  ).trim();
  return status ? `${name} — ${status}` : name;
};

// The list is already the governed selector/allowlist. Lifecycle controls
// whether a member contributes delivery, not whether its authorized flight is
// part of the explicit ALL CAMPAIGNS scope.
export const campaignsIncludedInAll = (campaigns = []) =>
  Array.isArray(campaigns) ? campaigns : [];

export const buildCampaignScopeFlightWindow = (
  campaigns = [],
  selectedCampaignIdentity = ""
) => {
  const list = Array.isArray(campaigns) ? campaigns : [];
  const wanted = String(selectedCampaignIdentity || "");
  const scoped = wanted
    ? list.filter(
        (campaign) =>
          String(campaign?.key ?? campaign?.id ?? "") === wanted
      )
    : campaignsIncludedInAll(list);
  const starts = scoped
    .map(
      (campaign) =>
        campaign?.flightStartDate ||
        campaignReportingContract(campaign)?.flightStartDate
    )
    .filter(isoDay)
    .sort();
  // CAMPAIGN FLIGHT is the ORIGINAL APPROVED FLIGHT — immutable history. A
  // later authorized amendment never moves this end bound; the amended/actual
  // delivery lifecycle is the FULL DELIVERY window below.
  const ends = scoped
    .map(
      (campaign) =>
        campaign?.flightEndDate ||
        campaignReportingContract(campaign)?.flightEndDate
    )
    .filter(isoDay)
    .sort();
  if (!starts.length || !ends.length) return null;
  return { startDate: starts[0], endDate: ends[ends.length - 1] };
};

/*
 * The FULL DELIVERY window: everything the campaign actually delivered —
 * start through max(latest reported delivery, authorized delivery end,
 * original flight end), as the backend resolves it into fullDeliveryWindow.
 * A REPORTING view, never an authorization claim: it must not imply the
 * delivery inside it was authorized. Same scoping rules as the flight window.
 */
export const buildCampaignScopeFullDeliveryWindow = (
  campaigns = [],
  selectedCampaignIdentity = ""
) => {
  const list = Array.isArray(campaigns) ? campaigns : [];
  const wanted = String(selectedCampaignIdentity || "");
  const scoped = wanted
    ? list.filter(
        (campaign) =>
          String(campaign?.key ?? campaign?.id ?? "") === wanted
      )
    : campaignsIncludedInAll(list);
  const starts = scoped
    .map(
      (campaign) =>
        campaign?.fullDeliveryWindow?.start ||
        campaign?.flightStartDate ||
        campaignReportingContract(campaign)?.flightStartDate
    )
    .filter(isoDay)
    .sort();
  const ends = scoped
    .map(
      (campaign) =>
        campaign?.fullDeliveryWindow?.end ||
        campaign?.authorizedDeliveryEndDate ||
        campaignReportingContract(campaign)?.authorizedDeliveryEndDate ||
        campaign?.flightEndDate ||
        campaignReportingContract(campaign)?.flightEndDate
    )
    .filter(isoDay)
    .sort();
  if (!starts.length || !ends.length) return null;
  return { startDate: starts[0], endDate: ends[ends.length - 1] };
};

// Provenance for a client-authorized Full Delivery extension: the original
// approved flight (history, never rewritten) beside the authorized delivery
// window that now governs execution. Null unless the campaign is actually
// extended and carries a well-formed authorized delivery end.
export const campaignDeliveryExtensionProvenance = (campaign) => {
  const contract = campaignReportingContract(campaign);
  const extended =
    campaign?.deliveryExtended === true || contract?.deliveryExtended === true;
  const authorizedEnd =
    campaign?.authorizedDeliveryEndDate ||
    contract?.authorizedDeliveryEndDate ||
    "";
  if (!extended || !isoDay(authorizedEnd)) return null;
  const flightStart =
    campaign?.flightStartDate || contract?.flightStartDate || "";
  const flightEnd = campaign?.flightEndDate || contract?.flightEndDate || "";
  return {
    flightStartDate: isoDay(flightStart) ? flightStart : null,
    flightEndDate: isoDay(flightEnd) ? flightEnd : null,
    authorizedDeliveryEndDate: authorizedEnd,
  };
};

/*
 * Provenance for the FULL DELIVERY reporting view: the original approved
 * flight (history), the authorized extension when one was approved, and the
 * governed admission when reported delivery ran beyond any recorded
 * authorization. Null when the campaign carries neither a fullDeliveryWindow
 * nor an extension — there is nothing beyond the flight to explain.
 */
export const campaignFullDeliveryProvenance = (campaign) => {
  const contract = campaignReportingContract(campaign);
  const fullDeliveryWindow =
    campaign?.fullDeliveryWindow && typeof campaign.fullDeliveryWindow === "object"
      ? campaign.fullDeliveryWindow
      : null;
  const extension = campaignDeliveryExtensionProvenance(campaign);
  if (!fullDeliveryWindow && !extension) return null;
  const flightStart =
    campaign?.flightStartDate || contract?.flightStartDate || "";
  const flightEnd = campaign?.flightEndDate || contract?.flightEndDate || "";
  return {
    flightStartDate: isoDay(flightStart) ? flightStart : null,
    flightEndDate: isoDay(flightEnd) ? flightEnd : null,
    // Only an approved amendment may claim an authorized delivery end here.
    authorizedDeliveryEndDate: extension?.authorizedDeliveryEndDate || null,
    deliveryBeyondAuthorization: Boolean(
      fullDeliveryWindow?.deliveryBeyondAuthorization
    ),
  };
};

export const campaignNoDeliveryState = (campaign) => {
  const contract = campaignReportingContract(campaign);
  if (!contract) return null;
  if (
    contract.lifecycleKey !== "pre_launch" &&
    contract.reportingStateKey !== "not_yet_delivering"
  ) {
    return null;
  }
  return contract.emptyState || {
    key: "campaign_not_yet_live",
    title: "Campaign not yet live",
    message: "No delivery reported yet.",
  };
};
